import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '../services/dashboardService';
import type {
  DashboardAlert,
  LocationDashboardSummary,
  LocationDashboardSettings,
} from '@/shared/types/domain.types';

/**
 * Dashboard query keys factory
 */
export const dashboardKeys = {
  all: ['dashboard'] as const,
  alerts: (locationIds: string[]) => [...dashboardKeys.all, 'alerts', locationIds.sort()] as const,
  summary: (locationIds: string[]) => [...dashboardKeys.all, 'summary', locationIds.sort()] as const,
  settings: (locationId: string) => [...dashboardKeys.all, 'settings', locationId] as const,
};

/**
 * Fetch dashboard alerts for given locations
 * Returns only yellow/red alerts (exception-based)
 *
 * @param locationIds - Array of location IDs to fetch alerts for
 * @param enabled - Whether the query should run (default: true)
 */
export function useDashboardAlerts(locationIds: string[], enabled = true) {
  return useQuery<DashboardAlert[], Error>({
    queryKey: dashboardKeys.alerts(locationIds),
    queryFn: () => dashboardService.getDashboardAlerts(locationIds),
    enabled: enabled && locationIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - dashboard data doesn't change rapidly
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection time
  });
}

/**
 * Fetch multi-location summary for regional managers
 * Returns table data with key metrics per location
 *
 * @param locationIds - Array of location IDs to fetch summary for
 * @param enabled - Whether the query should run (default: true)
 */
export function useMultiLocationSummary(locationIds: string[], enabled = true) {
  return useQuery<LocationDashboardSummary[], Error>({
    queryKey: dashboardKeys.summary(locationIds),
    queryFn: () => dashboardService.getMultiLocationSummary(locationIds),
    enabled: enabled && locationIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch dashboard settings for a specific location
 *
 * @param locationId - Location ID to fetch settings for
 * @param enabled - Whether the query should run (default: true)
 */
export function useLocationDashboardSettings(locationId: string, enabled = true) {
  return useQuery<LocationDashboardSettings, Error>({
    queryKey: dashboardKeys.settings(locationId),
    queryFn: () => dashboardService.getLocationSettings(locationId),
    enabled: enabled && !!locationId,
    staleTime: 10 * 60 * 1000, // 10 minutes - settings change rarely
    gcTime: 30 * 60 * 1000,
  });
}
